import structuredClone from '@ungap/structured-clone';
import { MonetizableEnum } from '@repo/supergraph/marketplace/enums';
import type {
  BaseMarketplaceEntityType,
  StateAbbreviation,
} from '@repo/supergraph/marketplace/types';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import { startCase, toLower } from 'lodash';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useOffersFilterStore } from '.';
import { STATE_OPTIONS } from '../../../../GeoTargeting/constants';
import {
  FILTER_PAIR_MAP,
  LABEL_TO_PRODUCT_TYPES,
  MIN_BALANCE_RANGES,
  MIN_DEPOSIT_RANGES,
  rangeOptionsByField,
  TERM_LENGTH_RANGES,
} from './constants';
import { OffersFilterStoreState } from './store';
import type { FilterEnabledSlugs, marketplaceEntityFilterFieldNames } from './types';
import { parseMonetaryValue, parsePercentageValue } from './utils';

const SORT_FIELD_CONFIG = {
  driverBonus: {
    // The sort option id remains `driverBonus`, but sorting must use the non-override value since the overide value can contain more than just the bonus value.
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.driverBonusWithoutOverride?.value,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'string') return parseMonetaryValue(value);
      return 0;
    },
    ascending: false,
  },
  monthlyFee: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.driverMonthlyFee?.value,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'string') return parseMonetaryValue(value);
      return 0;
    },
    ascending: true,
  },
  apy: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.apy,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      return 0;
    },
    ascending: false,
  },
  starRating: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.starRating,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      return 0;
    },
    ascending: false,
  },
  minimumBalance: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.minimumBalance,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      return 0;
    },
    ascending: true,
  },
  cdDepositTerm: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.cdDepositTerm,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      return 0;
    },
    ascending: true,
  },
  driverApy: {
    propertyPath: (entity: BaseMarketplaceEntityType) => entity?.driverApy?.value,
    parser: (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') return parsePercentageValue(value);
      return 0;
    },
    ascending: false,
  },
} as const;

function hasBonus(offer: ProductCardType): boolean {
  const bonusValue =
    offer.marketplaceEntity.driverBonus?.value ||
    offer.marketplaceEntity.driverBonusWithoutOverride?.value;
  return !!(bonusValue && bonusValue !== '$0' && bonusValue !== '0' && bonusValue !== 'N/A');
}

function matchesBonusFilter(offer: ProductCardType, selectedOptions: string[]): boolean {
  if (selectedOptions.length === 0) return true;
  const hasBonusValue = hasBonus(offer);
  return selectedOptions.some(option => {
    if (option === 'Bonus') return hasBonusValue;
    if (option === 'No Bonus') return !hasBonusValue;
    return false;
  });
}

const getSortFieldConfig = (sortBy: string) => {
  return SORT_FIELD_CONFIG[sortBy as keyof typeof SORT_FIELD_CONFIG];
};

function isMonetizingOffer(offer: ProductCardType): boolean {
  return offer.marketplaceEntity.monetizable === MonetizableEnum.yesAssumed;
}

// APY-tie tiebreaker #1: monetizing offers outrank non-monetizing ones.
function compareByMonetizing(offerA: ProductCardType, offerB: ProductCardType): number {
  const monetizingA = isMonetizingOffer(offerA);
  const monetizingB = isMonetizingOffer(offerB);
  if (monetizingA === monetizingB) return 0;
  return monetizingA ? -1 : 1;
}

// APY-tie tiebreaker #2: longest term wins (highest total earnings at an equal APY).
function compareByTermLengthDescending(offerA: ProductCardType, offerB: ProductCardType): number {
  const termA = offerA.marketplaceEntity.cdDepositTerm;
  const termB = offerB.marketplaceEntity.cdDepositTerm;

  if (termA == null && termB == null) return 0;
  if (termA == null) return 1;
  if (termB == null) return -1;

  return termB - termA;
}

type FilterStrategy = (
  appliedFilters: OffersFilterStoreState['appliedFilters'],
  marketplaceEntity: ProductCardType['marketplaceEntity'],
) => boolean;

const bankBonusFiltersStrategy: FilterStrategy = (appliedFilters, marketplaceEntity) => {
  const selectedProductTypes = appliedFilters?.selectedProductTypes || [];

  // note: location filter is handled directly in the summary table and product cards as before introducing the new filters
  const meetsProductTypeRanges =
    selectedProductTypes.length === 0 ||
    selectedProductTypes.some(
      productType =>
        marketplaceEntity.subProductType ===
        LABEL_TO_PRODUCT_TYPES[productType as keyof typeof LABEL_TO_PRODUCT_TYPES],
    );
  return meetsProductTypeRanges;
};

const cdRatesFiltersStrategy: FilterStrategy = (appliedFilters, marketplaceEntity) => {
  const selectedTermRanges = appliedFilters?.selectedTermRanges || [];
  const selectedMinDepositRanges = appliedFilters?.selectedMinDepositRanges || [];

  if (selectedTermRanges.length === 0 && selectedMinDepositRanges.length === 0) {
    return true;
  }

  const meetsTermRanges =
    selectedTermRanges.length === 0 ||
    selectedTermRanges.some(rangeLabel => {
      const range = TERM_LENGTH_RANGES.find(r => r.label === rangeLabel);
      if (!range) return false;
      const term = marketplaceEntity.cdDepositTerm || 0;
      return term >= range.min && term <= range.max;
    });

  const meetsDepositRanges =
    selectedMinDepositRanges.length === 0 ||
    selectedMinDepositRanges.some(rangeLabel => {
      const range = MIN_DEPOSIT_RANGES.find(r => r.label === rangeLabel);
      if (!range) return false;
      const deposit = marketplaceEntity.minimumBalance || 0;
      return deposit >= range.min && deposit <= range.max;
    });

  return meetsTermRanges && meetsDepositRanges;
};

const geoTargetedPaidFiltersStrategy: FilterStrategy = () => true;

const hysaFiltersStrategy: FilterStrategy = (appliedFilters, marketplaceEntity) => {
  const selectedBonusOptions = appliedFilters?.selectedBonusOptions || [];
  const selectedMinBalanceRanges = appliedFilters?.selectedMinBalanceRanges || [];

  if (selectedBonusOptions.length === 0 && selectedMinBalanceRanges.length === 0) return true;

  const meetsBonusFilter = matchesBonusFilter(
    { marketplaceEntity } as ProductCardType,
    selectedBonusOptions,
  );

  const meetsBalanceRanges =
    selectedMinBalanceRanges.length === 0 ||
    selectedMinBalanceRanges.some(rangeLabel => {
      const range = MIN_BALANCE_RANGES.find(r => r.label === rangeLabel);
      if (!range) return false;
      const balance = marketplaceEntity.minimumBalance || 0;
      return balance >= range.min && balance <= range.max;
    });

  return meetsBonusFilter && meetsBalanceRanges;
};

const FILTER_STRATEGIES_BY_SLUG: Record<FilterEnabledSlugs, FilterStrategy> = {
  'bank-bonuses-and-promotions': bankBonusFiltersStrategy,
  'cd-rates': cdRatesFiltersStrategy,
  'paid-geo-targeted': geoTargetedPaidFiltersStrategy,
  'high-yield-online-savings-accounts': hysaFiltersStrategy,
};

export function getOfferFilterFunction(
  appliedFilters: OffersFilterStoreState['appliedFilters'],
  slug: FilterEnabledSlugs,
) {
  if (!Object.keys(FILTER_STRATEGIES_BY_SLUG).includes(slug)) {
    return () => true;
  }

  const strategy = FILTER_STRATEGIES_BY_SLUG[slug];

  return ({ marketplaceEntity }: ProductCardType) => {
    return strategy(appliedFilters, marketplaceEntity);
  };
}

export function useGetOffersFilterOffers() {
  const { allOffers, filtersApplied, appliedFilters, sortBy, slug } = useOffersFilterStore(
    ({ allOffers, filtersApplied, appliedFilters, sortBy, slug }) => ({
      allOffers,
      filtersApplied,
      appliedFilters,
      sortBy,
      slug,
    }),
    shallow,
  );

  const offers = useMemo(() => {
    const filteredOffers = filtersApplied
      ? allOffers.filter(getOfferFilterFunction(appliedFilters, slug))
      : structuredClone(allOffers);

    if (sortBy) {
      return filteredOffers.sort((offerA, offerB) => {
        const config = getSortFieldConfig(sortBy);
        if (!config) {
          throw new Error(
            `Missing sort configuration for field: ${sortBy}. Please add it to SORT_FIELD_CONFIG.`,
          );
        }

        const valueA = config.propertyPath(offerA.marketplaceEntity);
        const valueB = config.propertyPath(offerB.marketplaceEntity);

        // Missing values (null/undefined): sort after any real value so parsers' `0` fallback
        // does not float missing "lowest first" fields (e.g. monthlyFee, minimumBalance) to the top.
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return 1;
        if (valueB == null) return -1;

        const parsedA = config.parser(valueA);
        const parsedB = config.parser(valueB);

        const comparison = config.ascending ? parsedA - parsedB : parsedB - parsedA;
        if (comparison !== 0 || sortBy !== 'apy' || slug !== 'cd-rates') return comparison;

        // CD rates, APY tie: monetizing status first, then longest term.
        const monetizingComparison = compareByMonetizing(offerA, offerB);
        if (monetizingComparison !== 0) return monetizingComparison;

        return compareByTermLengthDescending(offerA, offerB);
      });
    }

    return filteredOffers;
  }, [allOffers, filtersApplied, appliedFilters, sortBy, slug]);

  return offers;
}

export function useFilterProductTypeOptions(regionCode?: StateAbbreviation) {
  const { allOffers, appliedFilters } = useOffersFilterStore(
    ({ allOffers, appliedFilters }) => ({
      allOffers,
      appliedFilters,
    }),
    shallow,
  );

  const rangeOptionsWithCounts = useMemo(() => {
    const selectedLocation = appliedFilters?.selectedLocation ?? regionCode;

    const offersFilteredByLocation = allOffers.filter(offer => {
      const validStates = offer.marketplaceEntity.states;
      return (
        !Array.isArray(validStates) ||
        validStates.length === 0 ||
        validStates.includes(selectedLocation as StateAbbreviation)
      );
    });

    const baseProductTypes = ['CHECKING', 'SAVINGS'];

    return baseProductTypes.map(productType => {
      const count = offersFilteredByLocation.filter(
        offer => offer.marketplaceEntity.subProductType === productType,
      ).length;
      const titleCase = startCase(toLower(productType));
      return {
        label: `${titleCase} Account`,
        value: productType,
        count,
      };
    });
  }, [appliedFilters, allOffers, regionCode]);

  return rangeOptionsWithCounts;
}

// Give the hook one filter field (e.g., Term Length) and it returns that field’s own list of options
//  plus how many offers fall in each option after the other filter’s current selections are applied.
export function useRangeFilterOptions(marketplaceFieldName: marketplaceEntityFilterFieldNames) {
  const { allOffers, appliedFilters } = useOffersFilterStore(
    ({ allOffers, appliedFilters }) => ({
      allOffers,
      appliedFilters,
    }),
    shallow,
  );

  const { peerFilterKey, peerMarketplaceKey } = FILTER_PAIR_MAP[marketplaceFieldName];

  const rangeOptionsWithCounts = useMemo(() => {
    const activePeerRanges = appliedFilters?.[peerFilterKey].length
      ? rangeOptionsByField[peerMarketplaceKey].filter(range =>
          appliedFilters[peerFilterKey].includes(range.label),
        )
      : null;

    const offersFilteredByPeer = activePeerRanges
      ? allOffers.filter(offer => {
          const minFieldValue = offer.marketplaceEntity[peerMarketplaceKey] ?? 0;
          return activePeerRanges.some(
            range => minFieldValue >= range.min && minFieldValue <= range.max,
          );
        })
      : structuredClone(allOffers);

    return rangeOptionsByField[marketplaceFieldName].map(range => {
      const count = offersFilteredByPeer.filter(offer => {
        const fieldValueInMarketplace = offer.marketplaceEntity[marketplaceFieldName] || 0;
        return fieldValueInMarketplace >= range.min && fieldValueInMarketplace <= range.max;
      }).length;

      return {
        ...range,
        count,
      };
    });
  }, [appliedFilters, allOffers]);

  return rangeOptionsWithCounts;
}

export function useFilterLocationOptions() {
  return STATE_OPTIONS.map(state => ({
    label: state.label,
    value: state.value,
  }));
}

export function useMinBalanceFilterOptions() {
  const { allOffers, appliedFilters } = useOffersFilterStore(
    ({ allOffers, appliedFilters }) => ({ allOffers, appliedFilters }),
    shallow,
  );

  return useMemo(() => {
    const selectedBonusOptions = appliedFilters?.selectedBonusOptions || [];
    const offersFilteredByPeer = selectedBonusOptions.length
      ? allOffers.filter(offer => matchesBonusFilter(offer, selectedBonusOptions))
      : allOffers;

    return MIN_BALANCE_RANGES.map(range => ({
      ...range,
      count: offersFilteredByPeer.filter(offer => {
        const balance = offer.marketplaceEntity.minimumBalance || 0;
        return balance >= range.min && balance <= range.max;
      }).length,
    }));
  }, [appliedFilters, allOffers]);
}

export function useBonusFilterOptions() {
  const { allOffers, appliedFilters } = useOffersFilterStore(
    ({ allOffers, appliedFilters }) => ({ allOffers, appliedFilters }),
    shallow,
  );

  return useMemo(() => {
    const selectedMinBalanceRanges = appliedFilters?.selectedMinBalanceRanges || [];
    const offersFilteredByPeer = selectedMinBalanceRanges.length
      ? allOffers.filter(offer => {
          const balance = offer.marketplaceEntity.minimumBalance || 0;
          return selectedMinBalanceRanges.some(rangeLabel => {
            const range = MIN_BALANCE_RANGES.find(r => r.label === rangeLabel);
            if (!range) return false;
            return balance >= range.min && balance <= range.max;
          });
        })
      : allOffers;

    const bonusCount = offersFilteredByPeer.filter(hasBonus).length;
    const noBonusCount = offersFilteredByPeer.filter(offer => !hasBonus(offer)).length;

    return [
      { label: 'Bonus', value: 'Bonus', count: bonusCount },
      { label: 'No Bonus', value: 'No Bonus', count: noBonusCount },
    ];
  }, [appliedFilters, allOffers]);
}
